import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import type { GraphQLClient } from 'graphql-request';
import { ClientError } from 'graphql-request';
import { getGraphQLClient } from '../graphqlClient';

type GraphQLQueryVariables = Record<string, unknown>;
type GraphQLMutationVariables = Record<string, unknown>;

function executeGraphQLRequest<TData, TVariables extends GraphQLQueryVariables>(
  client: GraphQLClient,
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables
): Promise<TData> {
  // @ts-expect-error - graphql-request type signature expects rest parameters but accepts object
  return client.request(document, variables) as Promise<TData>;
}

export interface QueryHookRuntimeOptions {
  refetchInterval?: number | false;
}

export function createQueryHook<TData, TVariables extends GraphQLQueryVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  queryKeyFactory: (
    variables: TVariables
  ) => readonly (string | number | boolean | null | undefined)[],
  options?: {
    enabled?: (variables: TVariables) => boolean;
    select?: (data: TData) => TData;
    staleTime?: number;
    refetchInterval?: number;
  }
) {
  return (variables: TVariables, runtimeOptions?: QueryHookRuntimeOptions) => {
    const client = getGraphQLClient();

    const refetchIntervalRaw =
      runtimeOptions?.refetchInterval !== undefined
        ? runtimeOptions.refetchInterval
        : options?.refetchInterval;
    const refetchInterval = refetchIntervalRaw === false ? false : refetchIntervalRaw;

    return useQuery<TData, Error, TData, readonly (string | number | boolean | null | undefined)[]>(
      {
        queryKey: queryKeyFactory(variables),
        queryFn: () => executeGraphQLRequest(client as GraphQLClient, query, variables),
        enabled: options?.enabled ? options.enabled(variables) : true,
        select: options?.select,
        staleTime: options?.staleTime,
        refetchInterval,
      }
    );
  };
}

const handleQueryInvalidation = async <TVariables extends GraphQLMutationVariables>(
  invalidateQueries: Array<{
    queryKey: (variables: TVariables) => readonly (string | number | boolean | null | undefined)[];
    predicate?: (query: { queryKey: unknown }) => boolean;
  }>,
  variables: TVariables,
  queryClient: ReturnType<typeof useQueryClient>
) => {
  for (const invalidateConfig of invalidateQueries) {
    if (invalidateConfig.predicate) {
      await queryClient.invalidateQueries({
        predicate: invalidateConfig.predicate,
      });
    } else {
      const queryKey = invalidateConfig.queryKey(variables);
      await queryClient.invalidateQueries({
        queryKey,
      });
    }
  }
};

const handleSuccessMessage = (
  messageApi: ReturnType<typeof App.useApp>['message'],
  successMessage?: string
) => {
  if (successMessage) {
    messageApi.success(successMessage);
  }
};

const handleSuccessCallback = async <TData, TVariables extends GraphQLMutationVariables>(
  onSuccess: ((data: TData, variables: TVariables) => void | Promise<void>) | undefined,
  data: TData,
  variables: TVariables
) => {
  if (onSuccess) {
    await onSuccess(data, variables);
  }
};

const handleError = (
  messageApi: ReturnType<typeof App.useApp>['message'],
  error: Error,
  errorMessage?: string,
  onError?: (error: Error) => void
) => {
  const errorMsg = errorMessage || `Error: ${error.message}`;
  void messageApi.error(errorMsg);

  if (onError) {
    onError(error);
  }

  if (Sentry.isInitialized() && error instanceof ClientError) {
    const response = error.response;
    const firstError = response?.errors?.[0];
    const errorCode = firstError?.extensions?.code as string | undefined;

    const shouldIgnore =
      errorCode &&
      (errorCode.toLowerCase().includes('validation') ||
        errorCode.toLowerCase().includes('permission-denied') ||
        errorCode.toLowerCase().includes('access-denied'));

    if (!shouldIgnore) {
      Sentry.captureException(error, {
        contexts: {
          mutation: {
            message: firstError?.message || error.message,
            code: errorCode,
            status: response?.status,
          },
        },
        tags: {
          errorType: 'mutation',
        },
      });
    }
  }
};

export function createMutationHook<TData, TVariables extends GraphQLMutationVariables>(
  mutation: TypedDocumentNode<TData, TVariables>,
  options?: {
    invalidateQueries?: Array<{
      queryKey: (
        variables: TVariables
      ) => readonly (string | number | boolean | null | undefined)[];
      predicate?: (query: { queryKey: unknown }) => boolean;
    }>;
    onSuccess?: (data: TData, variables: TVariables) => void | Promise<void>;
    onError?: (error: Error) => void;
    successMessage?: string;
    errorMessage?: string;
  }
) {
  return () => {
    const client = getGraphQLClient();
    const queryClient = useQueryClient();
    const { message } = App.useApp();

    return useMutation<TData, Error, TVariables, unknown>({
      meta: {
        skipSentryReport: true,
      },
      mutationFn: (variables: TVariables) =>
        executeGraphQLRequest(client as GraphQLClient, mutation, variables),
      onSuccess: async (data, variables) => {
        if (options?.invalidateQueries) {
          await handleQueryInvalidation(options.invalidateQueries, variables, queryClient);
        }

        handleSuccessMessage(message, options?.successMessage);
        await handleSuccessCallback(options?.onSuccess, data as TData, variables);
      },
      onError: (error) => {
        handleError(message, error, options?.errorMessage, options?.onError);
      },
    });
  };
}
